import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Image from "src/components/elements/Image";
import { isSVGString } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import HeaderMenuItem from "./HeaderMenuItem";
import { useParams } from "react-router-dom";

const HeaderMenuGroup = ({ data, group_id, searchTerm = "", mordernDesign = false, dropdownPosition = "bottom-left", handleClick = () => {} }) => {
  const [imageClassName, setImageClassName] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const { content_value } = useParams();
  const hasActiveSubmenu = Boolean(data?.submenu?.find((item) => item?.slug === content_value));
  const [isOpen, setIsOpen] = useState(dropdownPosition === "inline" && hasActiveSubmenu);
  const closeTimeout = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let visibleMenuItems = data.submenu || [];

    if (searchTerm) {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      visibleMenuItems = visibleMenuItems.filter((subitem) => subitem.name.toLowerCase().includes(lowercaseSearchTerm));
    }

    setMenuItems(visibleMenuItems);
  }, [data.submenu, searchTerm]);

  useEffect(() => {
    if (dropdownPosition === "inline" && hasActiveSubmenu) {
      setIsOpen(true);
    }
  }, [dropdownPosition, hasActiveSubmenu]);

  useEffect(() => {
    if (isOpen && dropdownPosition === "inline" && dropdownRef.current) {
      dropdownRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isOpen, dropdownPosition]);

  const isGroupActive = () => {
    return Boolean(data?.submenu?.find((item) => item?.slug === content_value));
  };

  const getDropdownPositionClass = () => {
    switch (dropdownPosition) {
      case "left":
        return "left-0";
      case "right":
        return "right-0 px-1 py-1";
      case "center":
        return "left-1/2 -translate-x-1/2";
      case "bottom-left":
        return "right-56 top-0";
      case "inline":
        return "relative mt-1 w-full px-2 py-1";
      default:
        return "left-0 top-9";
    }
  };

  const iconImage = () => {
    if (!data.image) return null;

    return (
      <div className={classNames("nav-icon mr-2 h-5 w-5 flex-shrink-0", isSVGString(data.image) ? "" : "flex items-center justify-center", imageClassName)}>
        {isSVGString(data.image) ? (
          <div
            dangerouslySetInnerHTML={{
              __html: data.image.replace(/stroke-width="[^"]*"/g, 'stroke-width="1.7"'),
            }}
          />
        ) : (
          <Image
            src={data.image}
            alt="Menu image"
            classes={imageClassName}
            onError={() => setImageClassName("hidden")}
          />
        )}
      </div>
    );
  };

  const openMenu = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setIsOpen(true);
  };

  const closeMenu = () => {
    if (dropdownPosition === "inline") {
      return;
    }

    closeTimeout.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  if (menuItems.length === 1 && !data.subMenuFilteredDown) {
    return (
      <HeaderMenuItem
        handleClick={handleClick}
        data={menuItems[0]}
        group_id={group_id}
        image={data?.image}
        searchTerm={searchTerm}
        mordernDesign={mordernDesign}
      />
    );
  }

  const buttonClasses = classNames(
    "group/dropdown flex min-h-10 w-full select-none items-center justify-between rounded px-3 transition-all",
    mordernDesign ? "hover:bg-leftNavActiveColor hover:text-leftNavActiveTextColor" : "hover:bg-headerMenuActiveBgColor hover:text-headerMenuActiveTextColor",
    isGroupActive() ? (mordernDesign ? "bg-leftNavActiveColor text-leftNavActiveTextColor" : "bg-headerMenuActiveBgColor text-headerMenuActiveTextColor") : mordernDesign ? "text-leftNavTextColor" : "text-headerTextColor",
  );

  return (
    <div
      className="relative overflow-visible"
      onMouseLeave={dropdownPosition === "inline" ? undefined : closeMenu}>
      <button
        type="button"
        className={buttonClasses}
        onClick={dropdownPosition === "inline" ? toggleMenu : openMenu}
        onMouseEnter={dropdownPosition === "inline" ? undefined : openMenu}>
        <div className="flex min-w-0 items-center">
          {iconImage()}
          <span className="truncate whitespace-nowrap text-sm font-medium">{data.name}</span>
        </div>
        <ChevronDownIcon className={classNames("h-4 w-4 stroke-2 transition-transform", isOpen ? "rotate-180" : "")} />
      </button>

      {dropdownPosition === "inline" ? (
        <div
          ref={dropdownRef}
          className={classNames("relative w-full", isOpen ? "block" : "hidden", mordernDesign ? "bg-leftNavColor" : "bg-headerColor", getDropdownPositionClass())}>
          <div className="space-y-1">
            {menuItems.map((subitem) => (
              <HeaderMenuItem
                key={subitem._id}
                data={subitem}
                image={subitem?.image}
                group_id={group_id}
                searchTerm={searchTerm}
                handleClick={handleClick}
                mordernDesign={mordernDesign}
              />
            ))}
          </div>
        </div>
      ) : (
        <div
          className={classNames(
            "absolute z-[9999] min-w-48 rounded-md shadow-lg ring-1 ring-black ring-opacity-5",
            isOpen ? "visible opacity-100" : "invisible opacity-0",
            "transition-all duration-150",
            mordernDesign ? "bg-leftNavColor" : "bg-headerColor",
            getDropdownPositionClass(),
          )}>
          <div className="space-y-1">
            {menuItems.map((subitem) => (
              <HeaderMenuItem
                key={subitem._id}
                data={subitem}
                image={subitem?.image}
                group_id={group_id}
                searchTerm={searchTerm}
                handleClick={handleClick}
                mordernDesign={mordernDesign}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderMenuGroup;
